/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import DriverCountPersonType from "pages/Front/Booking/components/BookingFilters/DriverCountPersonType";
import {PersonType} from "types/enums/PersonType";
import clsx from "clsx";
import {toast} from "react-toastify";
import BookingAction from "store/booking/BookingAction";
import {useTypedSelector} from "helpers/reducers";
import {useDispatch} from "useDispatch";
import {MinusIcon, PlusIcon} from "@heroicons/react/24/outline";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import {useForm} from "react-hook-form";
import {useDebouncedCallback} from "use-debounce";

const SellerPersonsCount = () => {
    const {t} = useTranslation();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const dispatch = useDispatch();
    const MAX_DRIVERS_COUNT = 500;
    const MIN_DRIVERS_COUNT_ADULT = 1;
    const MIN_DRIVERS_COUNT_CHILD = 0;

    const {register, setValue, reset, watch} = useForm({
        defaultValues: {
            driversCount: bookingFilter.adults,
            childrenCount: bookingFilter.children,
        },
    });

    const limitDriversCount = (count: number, type: string): number => {
        const minDriversCount = type === "childrenCount" ? MIN_DRIVERS_COUNT_CHILD : MIN_DRIVERS_COUNT_ADULT;
        const maxDriversCount = type === "childrenCount" ? MAX_DRIVERS_COUNT : MAX_DRIVERS_COUNT;
        return (count <= maxDriversCount && count >= minDriversCount)
            ? count
            : (count > maxDriversCount)
                ? maxDriversCount
                : minDriversCount;
    };

    const valueButtonClassName =
        "bg-cta-800 text-contrast-800 cursor-pointer" +
        "  hover:no-underline text-3xl flex-grow text-center flex flex-row justify-center items-center min-w-[40px] sm:min-w-[44px]";

    const setDriversCountValue = (driversCountValue: number, type: string) => {
        if (isNaN(+driversCountValue)) {
            toast.warning(t("Enter numbers only"));
            return;
        }
        const limit = limitDriversCount(driversCountValue, type);
        if (type == "childrenCount") {
            setValue("childrenCount", limit);
        } else {
            setValue("driversCount", limit);
        }
        // setDriversCountHandler(limitDriversCount(limit));
    };
    const debouncedChangeHandler = useDebouncedCallback((driversCountValue: number, type: string) => setDriversCountValue(driversCountValue, type), 300);

    const driversCount = watch("driversCount");
    const childrenCount = watch("childrenCount");

    const setDriversCountHandler = (newDriversCount: number, type: string) => {
        console.log("newDriversCount", newDriversCount)
        const newCount = (type === "childrenCount" ? +childrenCount : +driversCount) + newDriversCount;
        setDriversCountValue(newCount, type);
    };

    const driversCountDispatch = (count, childrenCount) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                adults: count,
                children: childrenCount,
            })
        );
    };

    useEffect(() => {
        driversCountDispatch(driversCount, childrenCount);
    }, [driversCount, childrenCount]);

    return (
        <BookingCard className={"md:max-w-lg w-full mx-auto !px-0 !py-0"}>
            <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={"Number of people"} />

            <div className={"flex flex-col sm:flex-row sm:gap-4 items-center sm:items-start sm:justify-between px-3 py-3"}>
                <DriverCountPersonType
                    isAction={false}
                    isEvent={true}
                    isGroupTypeChildren={false}
                    isMinimalistic={false}
                    minimumDriversCount={MIN_DRIVERS_COUNT_ADULT}
                    personType={PersonType.Adult}
                />

                <div
                    className={clsx(
                        "bg-cta-800 flex flex-row justify-between items-stretch h-10 w-1/2 md:w-1/2 max-w-[164px] border rounded-md overflow-hidden"
                    )}
                >
                    <button
                        className={valueButtonClassName}
                        id={"DecrementPersonsCount-" + "adult"}
                        type={"button"}
                        onClick={() => setDriversCountHandler(-1, "driversCount")}
                    >
                        <MinusIcon className={"pointer-events-none w-5 h-5 text-white"} />
                    </button>
                    <div className={"text-xl text-gray-800 bg-white leading-4 text-center flex flex-row justify-center items-center"}>
                        <input
                            {...register("driversCount")}
                            className="shrink w-full px-1 text-center h-full text-black hover:outline-cta-800 hover:appearance-none	"
                            inputMode={"numeric"}
                            max={MAX_DRIVERS_COUNT}
                            min={MIN_DRIVERS_COUNT_ADULT}
                            style={{
                                appearance: "textfield",
                                WebkitAppearance: "none",
                                MozAppearance: "textfield",
                            }}
                            type={"number"}
                            onChange={e => debouncedChangeHandler(+e.target.value, "driversCount")}
                        />
                    </div>
                    <button
                        className={valueButtonClassName}
                        id={"IncrementPersonsCount-" + "adult"}
                        type={"button"}
                        onClick={() => setDriversCountHandler(+1, "driversCount")}
                    >
                        <PlusIcon className={"pointer-events-none w-5 h-5 text-white"} />
                    </button>
                </div>
            </div>
            <div className={"flex flex-col sm:flex-row sm:gap-4 items-center sm:items-start sm:justify-between px-3 py-3"}>
                <DriverCountPersonType
                    isAction={true}
                    isEvent={false}
                    isGroupTypeChildren={false}
                    isMinimalistic={false}
                    minimumDriversCount={MIN_DRIVERS_COUNT_CHILD}
                    personType={PersonType.Child}
                />

                <div
                    className={clsx(
                        "bg-cta-800 flex flex-row justify-between items-stretch h-10 w-1/2 md:w-1/2 max-w-[164px] border rounded-md overflow-hidden"
                    )}
                >
                    <button
                        className={valueButtonClassName}
                        id={"DecrementPersonsCount-" + "adult"}
                        type={"button"}
                        onClick={() => setDriversCountHandler(-1, "childrenCount")}
                    >
                        <MinusIcon className={"pointer-events-none w-5 h-5 text-white"} />
                    </button>
                    <div className={"text-xl text-gray-800 bg-white leading-4 text-center flex flex-row justify-center items-center"}>
                        <input
                            {...register("childrenCount")}
                            className="shrink w-full px-1 text-center h-full text-black hover:outline-cta-800 hover:appearance-none	"
                            inputMode={"numeric"}
                            max={MAX_DRIVERS_COUNT}
                            min={MIN_DRIVERS_COUNT_CHILD}
                            style={{
                                appearance: "textfield",
                                WebkitAppearance: "none",
                                MozAppearance: "textfield",
                            }}
                            type={"number"}
                            onChange={e => debouncedChangeHandler(+e.target.value, "childrenCount")}
                        />
                    </div>
                    <button
                        className={valueButtonClassName}
                        id={"IncrementPersonsCount-" + "adult"}
                        type={"button"}
                        onClick={() => setDriversCountHandler(+1, "childrenCount")}
                    >
                        <PlusIcon className={"pointer-events-none w-5 h-5 text-white"} />
                    </button>
                </div>
            </div>
        </BookingCard>
    );
};

export default SellerPersonsCount;
